import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import WorkplaceCalendar, {
	WorkplaceCalendarParams,
} from '../../../../Components/Calendar/WorkplaceCalendar';
import {
	WorkplaceCalendarWithFilterDefaultValues,
	WorkplaceCalendarWithFilterValues,
} from './WorkplaceCalendarFilter';
import styles from './WorkplaceCalendarWithFilter.module.scss';
import { useAppContext } from '../../../../Services/AppContext';
import WorkplaceCalendarToolbar from './WorkplaceCalendarToolbar';
import { Moment } from 'moment';
import { CalendarSettings } from '../../../../Components/Calendar/Calendar';
import useCustomSettings from '../../../Base/Hooks/useCustomSettings';
import { useEffectAsync } from '../../../../helpers';
import { CustomSettingKey } from '../../../Base/Enums/CustomSettingKey.enum';
import Loading from '../../../../Components/Loading';
import { CalendarViewIntervalEnum } from '../../Enums/CalendarViewInterval.enum';
import { CalendarView } from '../../Enums/CalendarView.eunm';
import { DEFAULT_SLOT_DURATION_OPTIONS } from '../../../Manager/Components/ManagerWrapper/Calendar/ManagerCalendarViewSettings/ManagerCalendarViewSettings';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import useTranslation from '../../../../Hooks/useTranslation';
import useFilter from '../../Hooks/useFilter';
import { formatHour } from '../../Helpers/formatSlotHour.helper';

interface Props {
	workplaceId: string;
	patientId?: string;
	params?: WorkplaceCalendarParams;
}

const WorkplaceCalendarWithFilter: React.FC<Props> = ({
	workplaceId,
	patientId,
	params,
}) => {
	const [calendarParams, setCalendarParams] = useState<
		WorkplaceCalendarParams | undefined
	>(params);
	const { userData } = useAppContext();
	const { setFilter, filter } = useFilter<WorkplaceCalendarWithFilterValues>({
		defaultFilter: params?.filter,
	});
	const [filterDefaultValues, setFilterDefaultValues] =
		useState<WorkplaceCalendarWithFilterDefaultValues>({ workplaceId });
	const wrapperRef = useRef<any>();
	const toolbarRef = useRef<any>();
	const [calendarHeight, setCalendarHeight] = useState<string>('100%');
	const [selectedDate, setSelectedDate] = useState<Moment | undefined>(
		undefined,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [settings, setSettings] = useState<CalendarSettings | undefined>(
		undefined,
	);
	const [slotDurationValue, setSlotDurationValue] = useState<
		SelectValue | undefined
	>(undefined);
	const { getCustomSettings } = useCustomSettings(workplaceId, userData.id);
	const { t } = useTranslation();

	const handleCalendarViewChange = (view: CalendarView) => {
		let newSlotDuration: SelectValue | undefined = undefined;
		if (view === CalendarView.TIME_GRID_DAY) {
			newSlotDuration = DEFAULT_SLOT_DURATION_OPTIONS.find(
				item => item.value === settings?.slotDurationDay,
			);
		} else if (view === CalendarView.TIME_GRID_WEEK) {
			newSlotDuration = DEFAULT_SLOT_DURATION_OPTIONS.find(
				item => item.value === settings?.slotDurationWeek,
			);
		}
		if (newSlotDuration) {
			setSlotDurationValue({
				value: newSlotDuration?.value,
				label: t(newSlotDuration?.label),
			});
		}
	};

	const preselectFilter = (doctorIds: { value: string }[]) => {
		const resolvedWorkplaceId = filter?.workplaceId ?? workplaceId;

		if (!resolvedWorkplaceId) {
			return;
		}
		setFilterDefaultValues({
			workplaceId: resolvedWorkplaceId,
			doctorIds,
		});
	};

	const handleCalculateDimensions = () => {
		setTimeout(() => {
			if (wrapperRef.current && toolbarRef.current) {
				const wrapperHeight = wrapperRef.current.clientHeight;
				const filterHeight = toolbarRef.current.clientHeight;
				setCalendarHeight(`${wrapperHeight - filterHeight}px`);
			}
		}, 100);
	};

	useLayoutEffect(() => {
		setTimeout(() => {
			handleCalculateDimensions();
		}, 1000);
	}, []);

	useEffect(() => {
		// @ts-ignore
		window.addEventListener('resize', handleCalculateDimensions);
		return () => {
			// @ts-ignore
			window.removeEventListener('resize', handleCalculateDimensions);
		};
	}, []);

	useEffect(() => {
		if (!params?.filter) return;

		const newFilterDefaults: WorkplaceCalendarWithFilterDefaultValues = {
			workplaceId,
		};

		if (params.filter?.doctorIds?.length) {
			newFilterDefaults.doctorIds = params.filter.doctorIds.map(id => ({
				value: id,
			}));
		}

		if (Object.keys(newFilterDefaults).length > 0) {
			setFilterDefaultValues(newFilterDefaults);
		}
	}, [params]);

	const handleSetDate = (date: Moment) => {
		setSelectedDate(date);
	};

	const handleSlotDurationChange = (slotDuration: CalendarViewIntervalEnum) => {
		setSettings(prev => {
			return {
				...prev,
				slotDurationDay: slotDuration,
				slotDurationWeek: slotDuration,
			};
		});
	};

	const handleLoadCustomSettings = async () => {
		setIsLoading(true);
		const settings = await getCustomSettings(
			CustomSettingKey.CALENDAR_SETTINGS,
		);

		if (settings) {
			setSettings({
				showWeekends: settings.showWeekends,
				highlightWorkingTime: settings.highlightWorkingTime,
				slotMinTime: formatHour(settings.calendarViewTime?.showCalendarFrom),
				slotMaxTime: formatHour(settings.calendarViewTime?.showCalendarTo),
				initialView: settings.defaultView?.value,
				forceMinimumEventHeight: settings.forceMinimumEventHeight,
				slotDurationDay: settings?.defaultSlotDurationDay?.value,
				slotDurationWeek: settings?.defaultSlotDurationWeek?.value,
				defaultEventLabel: settings?.defaultEventLabel?.value,
			});
		}

		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await handleLoadCustomSettings();
	}, []);

	const handleShowEventInCalendar = (eventId: string) => {
		setCalendarParams({ eventId });
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper} ref={wrapperRef}>
			<div ref={toolbarRef}>
				<WorkplaceCalendarToolbar
					setFilter={setFilter}
					filterDefaultValues={filterDefaultValues}
					onDateSelect={handleSetDate}
					onShowOnlyMe={() =>
						preselectFilter([
							{
								value: userData.id,
							},
						])
					}
					onShowAll={() => preselectFilter([])}
					workplaceId={workplaceId}
					filter={filter}
					onToolbarToggle={handleCalculateDimensions}
					onSlotDurationChange={handleSlotDurationChange}
					slotDurationValue={slotDurationValue}
					onCalendarSettingsChange={handleLoadCustomSettings}
					handleShowEventInCalendar={handleShowEventInCalendar}
				/>
			</div>
			<div style={{ height: calendarHeight, position: 'relative' }}>
				<WorkplaceCalendar
					workplaceId={workplaceId}
					filter={filter}
					params={calendarParams}
					patientId={patientId}
					preselectedDate={selectedDate}
					settings={settings}
					onViewChange={handleCalendarViewChange}
				/>
			</div>
		</div>
	);
};

export default WorkplaceCalendarWithFilter;
